import React, { type MouseEvent, useCallback, useState, useEffect, useRef, useMemo } from 'react';
import { styled } from '@compiled/react';
import noop from 'lodash/noop';
import WorkItemsIcon from '@atlaskit/icon/core/work-items';
import { Box } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import { useEpicIssueTypeName } from '@atlassian/jira-backlog-epics-store/src/selectors/index.tsx';
import { expVal } from '@atlassian/jira-feature-experiments';
import { useIntl } from '@atlassian/jira-intl';
import type { ColorSchema, Color } from '@atlassian/jira-issue-epic-color-types/src/types.tsx';
import { transformColorToTheme } from '@atlassian/jira-issue-epic-color/src/common/utils.tsx';
import { fireUIAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import { isVisualRefreshEnabled } from '@atlassian/jira-visual-refresh-rollout/src/feature-switch/index.tsx';
import {
	UNASSIGNED_EPIC_ID,
	DEFAULT_PARENT_ISSUE_TYPE_NAME,
	EPIC_CARD_DESCRIPTION_ID,
} from '../../../../../common/constants.tsx';
import Card from '../../../../../common/ui/card/main.tsx';
import ProgressBar from '../../../../../common/ui/progress-bar/main.tsx';
import { usePanelData } from '../../../../../context/use-panel-data.tsx';
import { EpicCardContents } from './card-contents/index.tsx';
import messages from './messages.tsx';
import type { EpicCardProps, ProgressBarComponentProps, TitleComponentProps } from './types.tsx';
import { useDropToAssignEpic } from './use-drop-to-assign-epic/index.tsx';

export const TitleComponent = ({
	isUnassigned,
	color,
	name,
	isActive,
	epicIssueTypeName,
}: TitleComponentProps) => {
	const { formatMessage } = useIntl();

	const { selectedColor, title } = useMemo(() => {
		const paletteColorValues: Record<Color, string> = transformColorToTheme(
			(colorSchema: ColorSchema) => colorSchema.primary,
		);
		const target =
			epicIssueTypeName && epicIssueTypeName !== DEFAULT_PARENT_ISSUE_TYPE_NAME
				? epicIssueTypeName
				: DEFAULT_PARENT_ISSUE_TYPE_NAME.toLowerCase();

		return {
			selectedColor: (color && paletteColorValues[color]) || '',
			title: isUnassigned
				? formatMessage(
						expVal('issue-terminology-refresh-m2-replace', 'isEnabled', false)
							? messages.unassignedIssueTermRefresh
							: messages.unassigned,
						{ target },
					)
				: name,
		};
	}, [color, epicIssueTypeName, formatMessage, isUnassigned, name]);

	const visualRefreshEnabled = isVisualRefreshEnabled();

	return (
		<Title visualRefresh={visualRefreshEnabled}>
			{isUnassigned && visualRefreshEnabled && (
				<Box
					paddingInline="space.050"
					paddingBlock="space.025"
					testId="software-backlog-panel.ui.epic.epic-panel.epic-card-list.epic-card.box"
				>
					<WorkItemsIcon color="currentColor" label="" />
				</Box>
			)}
			{selectedColor && (
				<ColorIndicator
					color={selectedColor}
					data-testid="software-backlog-panel.ui.epic.epic-panel.epic-card-list.epic-card.color-indicator"
					visualRefresh={visualRefreshEnabled}
				/>
			)}
			<TitleButton
				aria-pressed={isActive}
				aria-describedby={EPIC_CARD_DESCRIPTION_ID}
				visualRefresh={visualRefreshEnabled}
			>
				{title}
			</TitleButton>
		</Title>
	);
};

export const ProgressBarComponent = ({
	hasMessage,
	isLoading,
	isUnassigned,
	epicIssueTypeName,
	selectedIssues,
	status,
}: ProgressBarComponentProps) => {
	const { formatMessage } = useIntl();

	return isUnassigned ? null : (
		<ProgressBar
			hasMessage={hasMessage}
			progress={status}
			isLoading={isLoading}
			confirmMessage={formatMessage(
				expVal('issue-terminology-refresh-m2-replace', 'isEnabled', false)
					? messages.confirmMessageIssueTermRefresh
					: messages.confirmMessage,
				{
					numberOfIssues: selectedIssues.length,
					issueKey: selectedIssues.length > 0 ? selectedIssues[0].key : '',
					epicIssueTypeName,
				},
			)}
		/>
	);
};

export const EpicCard = ({
	isLoading = false,
	isDragging = false,
	isActive = false,
	isDragHandle = false,
	selectedIssues = [],
	epic,
	progress,
	estimation,
	isDropAllowed,
	onClick = noop,
	onDetailClick = noop,
	onCreateIssueClick = noop,
	onAssignEpic = noop,
	onRefetch,
}: EpicCardProps) => {
	const { boardId, isCompanyManaged } = usePanelData();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const [epicIssueTypeName] = useEpicIssueTypeName();
	const [hasMessage, setHasMessage] = useState(false);

	const { id, color, name, startDate, dueDate, hasError } = epic;
	const isUnassigned = id === UNASSIGNED_EPIC_ID;
	const { status, totalIssues } = progress;
	const toggleMessage = (messageStatus: boolean, isMessageUsed = true) => {
		isMessageUsed && setHasMessage(messageStatus);
	};

	useEffect(() => {
		toggleMessage(false);
	}, [status.toDo, status.inProgress, status.done]);

	const onViewDetailsClick = useCallback(() => onDetailClick(id), [id, onDetailClick]);

	const onClickEvent = useCallback(
		(_e: MouseEvent) => {
			const analyticsEvent = createAnalyticsEvent({
				action: 'clicked',
				actionSubject: 'droppableCard',
			});
			fireUIAnalytics(analyticsEvent, { epicCardId: id, isSelected: !isActive });
			onClick(id);
		},
		[isActive, onClick, id, createAnalyticsEvent],
	);

	const epicCardRef = useRef<HTMLDivElement | null>(null);
	const { isDraggingOver } = useDropToAssignEpic({
		epicId: id,
		boardId,
		ref: epicCardRef,
		isCompanyManaged,
		selectedIssues,
		isDropAllowed,
		toggleMessage,
		onAssignEpic,
	});

	return (
		<Card
			id={epic.id}
			title={
				<TitleComponent
					isUnassigned={isUnassigned}
					color={color}
					name={name}
					epicIssueTypeName={epicIssueTypeName}
					isActive={isActive}
				/>
			}
			subtitle={
				<ProgressBarComponent
					hasMessage={hasMessage}
					isLoading={isLoading}
					isUnassigned={isUnassigned}
					epicIssueTypeName={epicIssueTypeName}
					selectedIssues={selectedIssues}
					status={status}
				/>
			}
			isActive={isActive}
			isDragging={isDragging}
			isDraggingOver={isDraggingOver}
			onClick={onClickEvent}
			innerRef={epicCardRef}
			itemName={name}
			itemType="epic"
		>
			{id !== UNASSIGNED_EPIC_ID && !isDragHandle ? (
				<EpicCardContents
					isActive={isActive}
					isLoading={isLoading}
					epic={epic}
					progress={progress}
					estimation={estimation}
					hasError={hasError}
					startDate={startDate}
					dueDate={dueDate}
					totalIssues={totalIssues}
					onDetailClick={onDetailClick}
					onCreateIssueClick={onCreateIssueClick}
					onViewDetailsClick={onViewDetailsClick}
					onRefetch={onRefetch}
				/>
			) : null}
		</Card>
	);
};

export default EpicCard;

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Title = styled.div<{
	visualRefresh: boolean;
}>({
	display: 'flex',
	alignItems: 'flex-start',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ColorIndicator = styled.div<{
	color: string;
	visualRefresh: boolean;
}>({
	flexShrink: 0,
	width: token('space.200'),
	height: token('space.200'),
	borderRadius: token('border.radius'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	background: (props) => props.color,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	margin: ({ visualRefresh }) =>
		`${visualRefresh ? token('space.025') : token('space.050')} ${visualRefresh ? token('space.050') : token('space.100')} 0 0`,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const TitleButton = styled.button<{ visualRefresh?: boolean }>({
	overflow: 'hidden',
	wordWrap: 'break-word',
	border: 'none',
	background: 'inherit',
	padding: 0,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles
	margin: ({ visualRefresh }) => (visualRefresh ? undefined : token('space.025')),
	color: 'inherit',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles
	font: ({ visualRefresh }) => (visualRefresh ? token('font.body') : token('font.heading.xsmall')),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles
	fontWeight: ({ visualRefresh }) => (visualRefresh ? token('font.weight.regular') : undefined),
	textAlign: 'inherit',
});
